import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BiChevronLeft, BiChevronRight, BiInfoCircle, BiMinus, BiPlus, BiRightArrowAlt, BiX } from "react-icons/bi";
import { addMonths, daysInMonth, hourOf, isSameDay, istDay, partsOf, startOfDay, startOfMonth } from "../Helpers/dateRange";
import { createAvailability } from "../Helpers/carAvailability";

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABELS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const MONTH_SHORT = MONTH_LABELS.map((m) => m.slice(0, 3));
const PRESET_HOURS = [6, 12, 24, 72, 168];
const HOUR_TICKS = [0, 6, 12, 18, 23];
const MOBILE_MAX = 575;
const TWO_MONTH_MIN = 720;
const PANEL_WIDE = 680;
const PANEL_NARROW = 360;
const DAY_MS = 24 * 60 * 60 * 1000;
const NO_BUSY = [];

/* ---------------- formatting (IST, via dateRange.js) ---------------- */

const formatHour = (hour) => `${hour % 12 || 12}:00 ${hour < 12 ? "AM" : "PM"}`;
const formatClock = (date) => formatHour(partsOf(date).hour);
const formatDay = (date) => {
    const { day, month, weekday } = partsOf(date);
    return `${WEEKDAY_SHORT[weekday]}, ${day} ${MONTH_SHORT[month]}`;
};
const formatFull = (date) => `${formatDay(date)} · ${formatClock(date)}`;
const tickLabel = (hour) => (hour === 0 ? "12a" : hour === 12 ? "12p" : `${hour % 12}${hour < 12 ? "a" : "p"}`);
const plural = (n, unit) => `${n} ${unit}${n === 1 ? "" : "s"}`;

const formatDuration = (hours) => {
    const days = Math.floor(hours / 24);
    const rest = hours % 24;
    if (!days) return plural(rest, "hr");
    return rest ? `${days}d ${rest}h` : plural(days, "day");
};

const presetLabel = (hours) => {
    if (hours % 168 === 0) return plural(hours / 168, "week");
    if (hours % 24 === 0) return plural(hours / 24, "day");
    return plural(hours, "hr");
};

/** "BK12 (awaiting payment): Thu, 1 Oct · 9:00 AM → Sat, 3 Oct · 6:00 PM" */
const describeWindow = (w) =>
    `${w.bookingId || "Booking"}${w.stage === "Initiated" ? " (awaiting payment)" : ""}: ${formatFull(w.start)} → ${formatFull(w.end)}`;

/** Midnight IST `n` days after `day` (noon offset so rounding can never slip a day). */
const addDays = (day, n) => startOfDay(new Date(startOfDay(day).getTime() + n * DAY_MS + DAY_MS / 2));

const useViewportWidth = () => {
    const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
    useEffect(() => {
        const onResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    return width;
};

/* ---------------- hour control ---------------- */

const STATUS_COLOR = { ok: "var(--brp-track-on)", rule: "var(--brp-track-off)", busy: "var(--brp-track-busy)" };

// One colour band per run of equal hour statuses, positioned on the same
// thumb-centre scale as the slider so each band sits under its own hours.
const trackBackground = (statuses) => {
    const at = (fraction) => `calc(var(--brp-thumb) / 2 + (100% - var(--brp-thumb)) * ${fraction})`;
    const stops = [];
    let runStart = 0;
    for (let hour = 1; hour <= 24; hour++) {
        if (hour === 24 || statuses[hour] !== statuses[runStart]) {
            const color = STATUS_COLOR[statuses[runStart]];
            const from = runStart === 0 ? "0%" : at((runStart - 0.5) / 23);
            const to = hour === 24 ? "100%" : at((hour - 0.5) / 23);
            stops.push(`${color} ${from}`, `${color} ${to}`);
            runStart = hour;
        }
    }
    return `linear-gradient(to right, ${stops.join(", ")})`;
};

const HourControl = ({ id, label, day, value, statuses, onChange }) => {
    const okHours = statuses.map((s, h) => (s === "ok" ? h : null)).filter((h) => h !== null);
    const earlier = okHours.filter((h) => h < value).pop();
    const later = okHours.find((h) => h > value);
    const locked = okHours.length <= 1;
    const hasBusy = statuses.includes("busy");
    // Dragging onto an unavailable hour snaps to the nearest free one.
    const snap = (hour) => {
        let best = null;
        okHours.forEach((h) => { if (best === null || Math.abs(h - hour) < Math.abs(best - hour)) best = h; });
        if (best !== null && best !== value) onChange(best);
    };
    return (
        <div className="brp-hour">
            <div className="brp-hour-head">
                <div className="brp-hour-title">
                    <label htmlFor={id}>{label}</label>
                    <span>{day}</span>
                </div>
                <div className="brp-stepper">
                    <button type="button" className="brp-step-btn" onClick={() => earlier !== undefined && onChange(earlier)} disabled={earlier === undefined} aria-label={`${label}: earlier`}>
                        <BiMinus />
                    </button>
                    <output className="brp-hour-value" htmlFor={id} aria-live="polite">{formatHour(value)}</output>
                    <button type="button" className="brp-step-btn" onClick={() => later !== undefined && onChange(later)} disabled={later === undefined} aria-label={`${label}: later`}>
                        <BiPlus />
                    </button>
                </div>
            </div>
            <input
                id={id}
                type="range"
                className="brp-slider"
                min={0}
                max={23}
                step={1}
                value={value}
                disabled={locked}
                onChange={(event) => snap(Number(event.target.value))}
                aria-valuetext={formatHour(value)}
                style={{ background: trackBackground(statuses) }}
            />
            <div className="brp-ticks" aria-hidden="true">
                {HOUR_TICKS.map((hour) => (
                    <span key={hour} style={{ "--brp-at": hour / 23 }}>{tickLabel(hour)}</span>
                ))}
            </div>
            {(locked || hasBusy) && (
                <div className="brp-hour-note">
                    {hasBusy && <><span className="brp-swatch brp-swatch--busy" aria-hidden="true" />Booked hours are shaded red. </>}
                    {locked && `Only ${formatHour(value)} is available here.`}
                </div>
            )}
        </div>
    );
};

/* ---------------- the picker ---------------- */

/**
 * Optional props for a specific car (Register page - see CarBookingRangePicker):
 *   busy                 the car's booked windows; booked days/hours become unavailable
 *   availabilityLoading  true while those windows load (Apply waits for them)
 *   onOpen               called each time the panel opens (used to refresh `busy`)
 */
const BookingRangePicker = ({ value, onChange, dates, disabled = false, id, busy = NO_BUSY, availabilityLoading = false, onOpen }) => {
    const baseId = id || "booking-range";
    const viewport = useViewportWidth();
    const isMobile = viewport <= MOBILE_MAX;
    // Mobile sheet stacks two months vertically; desktop shows them side by side when there is room.
    const showTwoMonths = isMobile || viewport >= TWO_MONTH_MIN;

    const av = useMemo(() => createAvailability(dates, busy), [dates, busy]);

    const [open, setOpen] = useState(false);
    // Captured when the panel opens so the legal bounds can't drift under the user.
    const [now, setNow] = useState(() => new Date());
    const [draft, setDraft] = useState(() => dates.clampRange(value));
    // "start": the next day click begins a new range. "end": it picks the drop-off.
    const [phase, setPhase] = useState("start");
    // True right after a drop-off day was picked - only changes the helper text.
    const [completed, setCompleted] = useState(false);
    const [calendarStart, setCalendarStart] = useState(() => startOfMonth(new Date()));
    const [hoverDay, setHoverDay] = useState(null);
    const [focusDay, setFocusDay] = useState(null);
    const [panelBox, setPanelBox] = useState({ left: 0, width: PANEL_WIDE });

    const wrapperRef = useRef(null);
    const panelRef = useRef(null);
    const startSegRef = useRef(null);
    const endSegRef = useRef(null);
    const pendingFocusRef = useRef(null);
    // True while the drop-off hour hasn't been chosen by hand since the last pickup change.
    const autoEndRef = useRef(false);

    const committed = useMemo(() => dates.clampRange(value, now), [dates, value, now]);
    // The draft is re-fitted on every render too, so a rules change or newly
    // loaded bookings can never leave an illegal range on screen.
    const fitted = useMemo(() => {
        const clamped = dates.clampRange(draft, now);
        if (av.isRangeFree(clamped)) return { ...clamped, blocked: false };
        return av.fitRange(clamped, now);
    }, [av, dates, draft, now]);
    const current = useMemo(() => ({ start: fitted.start, end: fitted.end }), [fitted]);
    const blocked = fitted.blocked;
    const shown = open ? current : committed;

    const earliest = dates.earliestStart(now);
    const latest = dates.latestStart(now);
    const firstMonth = startOfMonth(earliest);
    const lastDay = startOfDay(dates.maxEndFor(latest));
    const lastMonth = startOfMonth(lastDay);
    const calendarEnd = addMonths(calendarStart, 1);
    const lastVisibleMonth = showTwoMonths ? calendarEnd : calendarStart;
    const canGoPrev = calendarStart.getTime() > firstMonth.getTime();
    const canGoNext = lastVisibleMonth.getTime() < lastMonth.getTime();

    const { minTripHours, maxTripMonths, maxHorizonMonths } = dates.rules;
    const tripHours = dates.tripHours(current);
    const ruleMaxTripHours = Math.round((dates.maxEndFor(current.start).getTime() - current.start.getTime()) / 36e5);
    const freeMaxTripHours = av.maxTripHoursFrom(current.start);
    const presets = [...new Set([minTripHours, ...PRESET_HOURS])].filter((h) => h >= minTripHours).sort((a, b) => a - b);

    /* ---------- visibility helpers ---------- */

    const isVisible = (day) => {
        const month = startOfMonth(day).getTime();
        return month === calendarStart.getTime() || (showTwoMonths && month === calendarEnd.getTime());
    };

    const reveal = (day) => {
        if (isVisible(day)) return;
        const month = startOfMonth(day);
        let next = month.getTime() < calendarStart.getTime() || !showTwoMonths ? month : addMonths(month, -1);
        if (next.getTime() < firstMonth.getTime()) next = firstMonth;
        setCalendarStart(next);
    };

    /* ---------- open / close ---------- */

    const openAt = (target) => {
        if (disabled) return;
        if (!open) {
            const freshNow = new Date();
            const fit = av.fitRange(value, freshNow);
            const fresh = { start: fit.start, end: fit.end };
            const anchor = target === "end" ? fresh.end : fresh.start;
            setNow(freshNow);
            setDraft(fresh);
            setCompleted(false);
            setHoverDay(null);
            setFocusDay(null);
            autoEndRef.current = false;
            const month = startOfMonth(target === "end" && showTwoMonths ? addMonths(fresh.end, -1) : anchor);
            const firstLegal = startOfMonth(dates.earliestStart(freshNow));
            setCalendarStart(month.getTime() < firstLegal.getTime() ? firstLegal : month);
            pendingFocusRef.current = startOfDay(anchor).getTime();
            setOpen(true);
            if (onOpen) onOpen();
        } else {
            reveal(target === "end" ? current.end : current.start);
        }
        setPhase(target);
    };

    const canApply = !blocked && !availabilityLoading;

    const close = (commit) => {
        if (commit && canApply) onChange(current);
        setOpen(false);
        setHoverDay(null);
    };

    // Latest `close` for the document listeners without re-binding them every render.
    const closeRef = useRef(close);
    closeRef.current = close;

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            // A click outside keeps what was picked - nothing is lost by accident.
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) closeRef.current(true);
        };
        const onKeyDown = (event) => {
            if (event.key !== "Escape") return;
            closeRef.current(false);
            (phase === "end" ? endSegRef.current : startSegRef.current)?.focus();
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open, phase]);

    // Bottom sheet: stop the page scrolling behind it.
    useEffect(() => {
        if (!open || !isMobile) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = previous; };
    }, [open, isMobile]);

    // Popover: keep it inside the viewport (shift left when the trigger sits near the right edge).
    useLayoutEffect(() => {
        if (!open || isMobile || !wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const width = Math.min(viewport - 32, showTwoMonths ? PANEL_WIDE : PANEL_NARROW);
        const overflow = viewport - 16 - (rect.left + width);
        const left = Math.max(16 - rect.left, Math.min(0, overflow));
        setPanelBox((box) => (box.left === left && box.width === width ? box : { left, width }));
    }, [open, isMobile, viewport, showTwoMonths]);

    // Move keyboard focus to a day once it has rendered.
    useEffect(() => {
        if (pendingFocusRef.current === null || !panelRef.current) return;
        const button = panelRef.current.querySelector(`[data-day="${pendingFocusRef.current}"]`);
        pendingFocusRef.current = null;
        if (button) button.focus({ preventScroll: true });
    });

    /* ---------- day rules ---------- */

    const startDay = startOfDay(current.start);
    const pickingEndOn = (day) => phase === "end" && day.getTime() >= startDay.getTime();

    const dayState = (day) => {
        if (pickingEndOn(day)) {
            const statuses = av.endHourStatus(day, current.start);
            if (statuses.includes("ok")) return { enabled: true };
            if (statuses.includes("busy")) {
                // Only days the car is actually booked get the "booked" look; days
                // that are merely beyond the next booking are plain unavailable.
                const limit = av.endLimit(current.start);
                return { enabled: false, booked: av.dayBooking(day) === "full", reason: `This car is booked again from ${formatFull(limit)} - drop-off must be before then` };
            }
            return day.getTime() < startOfDay(dates.minEndFor(current.start)).getTime()
                ? { enabled: false, reason: `Trips are at least ${plural(minTripHours, "hour")}` }
                : { enabled: false, reason: `Trips are at most ${plural(maxTripMonths, "month")}` };
        }
        const statuses = av.startHourStatus(day, now);
        if (statuses.includes("ok")) return { enabled: true };
        if (statuses.includes("busy")) {
            const windows = av.windowsOnDay(day);
            if (!windows.length) {
                return { enabled: false, reason: `No ${plural(minTripHours, "hour")} gap before this car's next booking` };
            }
            return { enabled: false, booked: true, reason: `Booked\n${windows.map(describeWindow).join("\n")}` };
        }
        return day.getTime() < startOfDay(earliest).getTime()
            ? { enabled: false, reason: `Earliest pickup is ${formatFull(earliest)}` }
            : { enabled: false, reason: `Pickups open up to ${plural(maxHorizonMonths, "month")} ahead` };
    };

    const selectDay = (day) => {
        if (!dayState(day).enabled) return;
        if (!pickingEndOn(day)) {
            // New pickup day: free hour closest to the current pickup hour, and
            // keep the trip length the user already had (clamped to what's free).
            const start = av.pickStart(day, hourOf(current.start), now);
            if (!start) return;
            setDraft(av.withLength(start, tripHours));
            setPhase("end");
            setCompleted(false);
            autoEndRef.current = true;
        } else {
            // Drop-off day: default to the pickup's hour after a new pickup (legacy behaviour).
            const preferred = autoEndRef.current ? hourOf(current.start) : hourOf(current.end);
            const end = av.pickEnd(day, current.start, preferred);
            if (!end) return;
            setDraft({ start: current.start, end });
            setPhase("start");
            setCompleted(true);
        }
        setHoverDay(null);
    };

    const onDayKeyDown = (event, day) => {
        const { weekday } = partsOf(day);
        const moves = {
            ArrowLeft: () => addDays(day, -1),
            ArrowRight: () => addDays(day, 1),
            ArrowUp: () => addDays(day, -7),
            ArrowDown: () => addDays(day, 7),
            Home: () => addDays(day, -weekday),
            End: () => addDays(day, 6 - weekday),
            PageUp: () => startOfDay(addMonths(day, -1)),
            PageDown: () => startOfDay(addMonths(day, 1)),
        };
        if (!moves[event.key]) return;
        event.preventDefault();
        let next = moves[event.key]();
        if (next.getTime() < startOfDay(firstMonth).getTime()) next = startOfDay(firstMonth);
        if (next.getTime() > lastDay.getTime()) next = lastDay;
        setFocusDay(next);
        if (phase === "end") setHoverDay(next);
        reveal(next);
        pendingFocusRef.current = next.getTime();
    };

    /* ---------- range preview ---------- */

    const hoverValid = phase === "end" && hoverDay && hoverDay.getTime() >= startDay.getTime() && dayState(hoverDay).enabled;
    const bandEnd = startOfDay(hoverValid ? hoverDay : current.end);
    const tentative = phase === "end";

    const focusTarget = startOfDay(focusDay || (phase === "end" ? current.end : current.start));
    const rovingDay = isVisible(focusTarget) ? focusTarget : calendarStart;

    const renderMonth = (monthAnchor) => {
        const { year, month } = partsOf(monthAnchor);
        const leading = partsOf(istDay(year, month, 1)).weekday;
        const total = daysInMonth(year, month);
        const cells = [];

        for (let i = 0; i < leading; i++) cells.push(<span key={`pad-${i}`} className="brp-day brp-day--pad" aria-hidden="true" />);

        for (let dayNo = 1; dayNo <= total; dayNo++) {
            const day = istDay(year, month, dayNo);
            const time = day.getTime();
            const { enabled, booked, reason } = dayState(day);
            const booking = av.hasBusy ? av.dayBooking(day) : null;
            const partial = enabled && booking !== null;
            const isStart = time === startDay.getTime();
            const isEnd = time === bandEnd.getTime();
            const isBetween = time > startDay.getTime() && time < bandEnd.getTime();
            const isToday = isSameDay(day, now);
            const title = reason || (partial ? `Partly booked\n${av.windowsOnDay(day).map(describeWindow).join("\n")}` : undefined);

            const classes = [
                "brp-day",
                !enabled && "brp-day--disabled",
                booked && "brp-day--booked",
                partial && "brp-day--partial",
                isToday && "brp-day--today",
                isBetween && "brp-day--between",
                isStart && "brp-day--start",
                isEnd && "brp-day--end",
                isStart && isEnd && "brp-day--single",
                dayNo === 1 && "brp-day--mfirst",
                dayNo === total && "brp-day--mlast",
            ].filter(Boolean).join(" ");

            const role = isStart && isEnd ? "pickup and drop-off" : isStart ? "pickup" : isEnd ? "drop-off" : "";
            cells.push(
                <button
                    type="button"
                    key={dayNo}
                    data-day={time}
                    className={classes}
                    tabIndex={time === rovingDay.getTime() ? 0 : -1}
                    aria-disabled={!enabled || undefined}
                    aria-pressed={isStart || isEnd}
                    aria-current={isToday ? "date" : undefined}
                    aria-label={`${WEEKDAY_SHORT[partsOf(day).weekday]} ${dayNo} ${MONTH_LABELS[month]} ${year}${role ? `, ${role}` : ""}${partial ? ", partly booked" : ""}${reason ? `, unavailable: ${reason.split("\n")[0]}` : ""}`}
                    title={title}
                    onClick={() => selectDay(day)}
                    onMouseEnter={() => phase === "end" && setHoverDay(day)}
                    onKeyDown={(event) => onDayKeyDown(event, day)}
                >
                    <span className="brp-day-num">{dayNo}</span>
                </button>
            );
        }

        return (
            <div className="brp-month" key={`${year}-${month}`}>
                <div className="brp-month-name" aria-live="polite">{MONTH_LABELS[month]} {year}</div>
                <div className="brp-weekdays" aria-hidden="true">
                    {WEEKDAY_LABELS.map((label) => <span key={label}>{label}</span>)}
                </div>
                <div className={`brp-days${tentative ? " brp-days--tentative" : ""}`} onMouseLeave={() => setHoverDay(null)}>
                    {cells}
                </div>
            </div>
        );
    };

    const switchPhase = (target) => {
        setPhase(target);
        setCompleted(false);
        setHoverDay(null);
        const anchor = target === "end" ? current.end : current.start;
        reveal(anchor);
        pendingFocusRef.current = startOfDay(anchor).getTime();
    };

    const applyPreset = (hours) => {
        autoEndRef.current = false;
        setDraft(av.withLength(current.start, hours));
        setPhase("start");
        setCompleted(true);
    };

    const reset = () => {
        const fit = av.defaultRange(now);
        setDraft({ start: fit.start, end: fit.end });
        setPhase("start");
        setCompleted(false);
        setCalendarStart(startOfMonth(fit.start));
    };

    const hint = availabilityLoading
        ? "Checking this car's bookings…"
        : phase === "end"
            ? "Now choose the drop-off day"
            : completed
                ? "Fine-tune the times below, or pick a new pickup day"
                : "Choose the pickup day";

    const activeSegment = open ? phase : null;

    const rulesNote = (
        <div className="brp-rules">
            <BiInfoCircle aria-hidden="true" />
            <span>
                Earliest pickup {formatFull(earliest)} &middot; trips {plural(minTripHours, "hr")} to {plural(maxTripMonths, "month")} &middot; up to {plural(maxHorizonMonths, "month")} ahead
            </span>
        </div>
    );

    return (
        <div className={`brp${open ? " brp--open" : ""}${disabled ? " brp--disabled" : ""}`} ref={wrapperRef}>
            <div className="brp-trigger">
                <button
                    type="button"
                    id={baseId}
                    ref={startSegRef}
                    className={`brp-seg${activeSegment === "start" ? " is-active" : ""}`}
                    onClick={() => openAt("start")}
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                >
                    <span className="brp-seg-label">Pickup</span>
                    <span className="brp-seg-day">{formatDay(shown.start)}</span>
                    <span className="brp-seg-time">{formatClock(shown.start)}</span>
                </button>
                <span className="brp-seg-mid" aria-hidden="true">
                    <BiRightArrowAlt />
                    <span className="brp-seg-duration">{formatDuration(dates.tripHours(shown))}</span>
                </span>
                <button
                    type="button"
                    ref={endSegRef}
                    className={`brp-seg brp-seg--end${activeSegment === "end" ? " is-active" : ""}`}
                    onClick={() => openAt("end")}
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    aria-label={`Drop-off ${formatFull(shown.end)} IST`}
                >
                    <span className="brp-seg-label">Drop-off</span>
                    <span className="brp-seg-day">{formatDay(shown.end)}</span>
                    <span className="brp-seg-time">{formatClock(shown.end)}</span>
                </button>
            </div>

            {open && isMobile && <div className="brp-backdrop" onMouseDown={() => close(true)} aria-hidden="true" />}

            {open && (
                <div
                    ref={panelRef}
                    className={`brp-panel${isMobile ? " brp-panel--sheet" : ""}`}
                    role="dialog"
                    aria-modal={isMobile || undefined}
                    aria-label="Choose pickup and drop-off"
                    style={isMobile ? undefined : { left: panelBox.left, width: panelBox.width }}
                >
                    {isMobile && <div className="brp-grabber" aria-hidden="true" />}
                    <div className="brp-panel-head">
                        <div>
                            <div className="brp-title">Trip dates</div>
                            <div className="brp-hint">{hint} <span className="brp-tz">All times IST</span></div>
                        </div>
                        <button type="button" className="brp-icon-btn" onClick={() => { close(false); startSegRef.current?.focus(); }} aria-label="Discard changes and close">
                            <BiX />
                        </button>
                    </div>

                    {blocked && (
                        <div className="brp-alert" role="alert">
                            This car has no free window long enough for a trip in the bookable period. Choose another car.
                        </div>
                    )}

                    <div className="brp-steps">
                        <button type="button" className={`brp-step${phase === "start" ? " is-active" : ""}`} onClick={() => switchPhase("start")} aria-pressed={phase === "start"}>
                            <span className="brp-step-label"><span className="brp-step-dot" />Pickup</span>
                            <span className="brp-step-value"><span>{formatDay(current.start)}</span><span className="brp-step-sep"> &middot; </span><span>{formatClock(current.start)}</span></span>
                        </button>
                        <span className="brp-step-duration">{formatDuration(tripHours)}</span>
                        <button type="button" className={`brp-step${phase === "end" ? " is-active" : ""}`} onClick={() => switchPhase("end")} aria-pressed={phase === "end"}>
                            <span className="brp-step-label"><span className="brp-step-dot brp-step-dot--end" />Drop-off</span>
                            <span className="brp-step-value"><span>{formatDay(current.end)}</span><span className="brp-step-sep"> &middot; </span><span>{formatClock(current.end)}</span></span>
                        </button>
                    </div>

                    <div className="brp-calendar">
                        <button type="button" className="brp-icon-btn brp-nav brp-nav--prev" onClick={() => canGoPrev && setCalendarStart(addMonths(calendarStart, -1))} disabled={!canGoPrev} aria-label="Previous month">
                            <BiChevronLeft />
                        </button>
                        <button type="button" className="brp-icon-btn brp-nav brp-nav--next" onClick={() => canGoNext && setCalendarStart(addMonths(calendarStart, 1))} disabled={!canGoNext} aria-label="Next month">
                            <BiChevronRight />
                        </button>
                        <div className="brp-months">
                            {renderMonth(calendarStart)}
                            {showTwoMonths && renderMonth(calendarEnd)}
                        </div>
                    </div>

                    {av.hasBusy && (
                        <div className="brp-legend" aria-hidden="true">
                            <span><span className="brp-legend-mark brp-legend-mark--booked">8</span>Booked</span>
                            <span><span className="brp-legend-mark brp-legend-mark--partial">8</span>Partly booked (hover for times)</span>
                            <span><span className="brp-legend-mark brp-legend-mark--off">8</span>Outside booking rules</span>
                        </div>
                    )}

                    <div className="brp-times">
                        <HourControl
                            id={`${baseId}-start-hour`}
                            label="Pickup time"
                            day={formatDay(current.start)}
                            value={hourOf(current.start)}
                            statuses={av.startHourStatus(current.start, now)}
                            onChange={(hour) => setDraft(av.setStartHour(hour, current, now))}
                        />
                        <HourControl
                            id={`${baseId}-end-hour`}
                            label="Drop-off time"
                            day={formatDay(current.end)}
                            value={hourOf(current.end)}
                            statuses={av.endHourStatus(current.end, current.start)}
                            onChange={(hour) => { autoEndRef.current = false; setDraft(av.setEndHour(hour, current)); }}
                        />
                    </div>

                    <div className="brp-presets" role="group" aria-label="Quick trip length">
                        <span className="brp-presets-label">Quick length</span>
                        {presets.map((hours) => {
                            const overRules = hours > ruleMaxTripHours;
                            const overBooking = !overRules && hours > freeMaxTripHours;
                            const active = tripHours === hours;
                            const title = overRules
                                ? `Trips are at most ${plural(maxTripMonths, "month")}`
                                : overBooking
                                    ? `This car is booked again from ${formatFull(av.endLimit(current.start))}`
                                    : `Drop-off ${formatFull(new Date(current.start.getTime() + hours * 36e5))}`;
                            return (
                                <button
                                    type="button"
                                    key={hours}
                                    className={`brp-chip${active ? " is-active" : ""}`}
                                    onClick={() => applyPreset(hours)}
                                    disabled={overRules || overBooking}
                                    aria-pressed={active}
                                    title={title}
                                >
                                    {presetLabel(hours)}
                                </button>
                            );
                        })}
                    </div>

                    {isMobile && rulesNote}

                    <div className="brp-foot">
                        {!isMobile && rulesNote}
                        <div className="brp-actions">
                            <button type="button" className="brp-btn brp-btn--ghost" onClick={reset}>Reset</button>
                            <button type="button" className="brp-btn brp-btn--primary" disabled={!canApply} onClick={() => { close(true); startSegRef.current?.focus(); }}>
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingRangePicker;
